import { ApplicationMimeTypesEnum } from "../../common/enums/ApplicationMimeTypesEnum";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { RouteFromObjectType } from "../../common/utilities/RouteFromObjectType";
import { FormFeatureEnum, FormTypeEnum, IFormInstanceData } from "../../views/object-form-view/Models";
import OfflineDataService from "../offline-data-service/OfflineDataService";
import restService from "../rest-service/RestService";
import { IFormTemplate } from "./TemplateModels";

 type FormsQueryParams = {
     groupID: number;
 };

type GetFormTemplatesQueryParams = {
    objectTypes: eLynxObjectTypeEnum[];
    includeDeleted?: boolean;
    returnProps?: string[]
}



enum QueryIntervalTypeEnum  {
        FormOpened = 0,
        FormClosed = 1,
        OpenForms = 2
};

type GetFormInstancesQueryParams =
{
    templateID?: number;
    queryStart?: Date;
    queryEnd?: Date;
    queryIntervalType?: QueryIntervalTypeEnum;
    includeDeleted?: boolean;
    maxCount?: number;
    returnProps?: string[];
    sessionToken?: string;

}

export interface IFormTemplateApiModel {
    id: number;
    version: number;
    name: string;
    formType: FormTypeEnum,
    formFeatures: FormFeatureEnum,
    createdBy: string;
    createdTimestamp: Date;
    objectTypes: eLynxObjectTypeEnum[];
    template: string;
    nextInstanceID: number;
    isDeleted: boolean;
}

export interface ICustomFormsExportOptions {
    mimeType: ApplicationMimeTypesEnum;
    notificationKey: string;
    beginDate: Date;
    endDate: Date;
    exportObject: IeLynxObject
}

const CustomFormsDataService = {

    getTemplates: async function(customerID: number,
        contextGroupID: number, 
        objectType: eLynxObjectTypeEnum,
        offline: boolean,
        abortSignal?: AbortSignal): Promise<IFormTemplate[]> {


        if (!offline) {

            const queryParams: GetFormTemplatesQueryParams = {
                objectTypes: [objectType],
                includeDeleted: true
            }

            const path = `/custom-forms/templates`;
            const response = await restService.post<GetFormTemplatesQueryParams, IFormTemplateApiModel[], FormsQueryParams>(
                path,
                queryParams,
                abortSignal, 
                undefined, {
                    groupID: contextGroupID
                }
            );

            return response.data.map(o => {
                return {
                    id: o.id,
                    version: o.version,
                    isDeleted: o.isDeleted,
                    name: o.name,
                    formType: o.formType,
                    formFeatures: o.formFeatures,
                    createdBy: o.createdBy,
                    createdTimestamp: new Date(o.createdTimestamp),
                    objectTypes: o.objectTypes,
                    template: JSON.parse(o.template),
                    nextInstanceID: o.nextInstanceID
                } as IFormTemplate
            });

        }   else {
            const offlineData = await OfflineDataService.getFormTemplates(customerID, contextGroupID, objectType);
            return offlineData.map(o => {
                return {
                    id: o.id,
                    name: o.name,
                    formType: o.formType,
                    formFeatures: o.formFeatures,
                    objectTypes: o.objectTypes,
                    template: JSON.parse(o.template)
                } as IFormTemplate
            });
        }         
    },

    getInstanceData: async function(customerID: number,
        contextGroupID: number, 
        eLynxObject: IeLynxObject,
        offline: boolean,
        startTime?: Date | undefined,
        endTime?: Date,
        sessionToken?: string,
        abortSignal?: AbortSignal): Promise<IFormInstanceData[]> {


        const queryParams: GetFormInstancesQueryParams = {
            queryStart: startTime,
            queryEnd: endTime,
            //sessionToken: sessionToken
        }

        if (!offline) {

            const path = eLynxObject.objectType === eLynxObjectTypeEnum.Group ? 
            `groups/${contextGroupID}/custom-forms/instances` :
            `groups/${contextGroupID}/${RouteFromObjectType(eLynxObject)}/${eLynxObject.id}/custom-forms/instances`;
            const response = await restService.post<GetFormInstancesQueryParams, IFormInstanceData[]>(
                path,
                queryParams,
                abortSignal, 
            );

            response.data.forEach(o => {
                o.openedTimestamp = new Date(o.openedTimestamp);
                o.closedTimestamp = o.closedTimestamp ? new Date(o.closedTimestamp) : undefined;
                o.history.forEach(v => v.modifiedTimestamp = new Date(v.modifiedTimestamp));
            });

            return response.data;

        }   else {

            return OfflineDataService.getPendingFormInstances(customerID);
    
        }         
    },
    saveInstance: async function(customerID: number,
        contextGroupID: number,
        formInstance: IFormInstanceData,
        offline: boolean,
        abortSignal?: AbortSignal): Promise<string> {

        if (!offline) {
            const path = 'custom-forms/instances';
            const response = await restService.post<IFormInstanceData, string, FormsQueryParams>(path, 
                formInstance,
                abortSignal,
                undefined,
                {
                    groupID: contextGroupID
                });

            return response.data;
        } else {
            await OfflineDataService.savePendingFormInstances(customerID, [formInstance]);
            return '';
        }
    },
    // This is for offline data support    
    saveInstances: async function (
        customerID: number,
        formInstances: IFormInstanceData[],
        offline: boolean,
        abortSignal?: AbortSignal
    ): Promise<void> {
        if (!offline) {
            const path = 'custom-forms/instances/batch';
            await restService.post<IFormInstanceData[], string>(path, 
                formInstances,
                abortSignal);
        } else {
            await OfflineDataService.savePendingFormInstances(customerID, formInstances);
        }
    },

    deleteInstance: async function(customerID: number,
        contextGroupID: number,
        formInstance: IFormInstanceData,
        offline: boolean,
        abortSignal?: AbortSignal): Promise<string> {

        if (!offline) {
            const path = `custom-forms/instances/${formInstance.id}`;
            const response = await restService.delete<string, FormsQueryParams>(path, 
                abortSignal,
                {
                    groupID: contextGroupID
                });

            return response.data;
        } else {
            await OfflineDataService.deletePendingFormInstance(customerID, formInstance);
            return ''
        }
    },
    export: async function (contextGroupID: number, 
        objectID: number, 
        objectType: eLynxObjectTypeEnum,
        startDate: Date, 
        endDate: Date,
        notificationKey: string, 
        mimeType: ApplicationMimeTypesEnum,
        abortSignal?: AbortSignal): Promise<string> {

        //date.day(1);  this is the first day of the week
        const start = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
        const end = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate() + 1);

        const exportParams: ICustomFormsExportOptions = {
            mimeType: mimeType,
            notificationKey: notificationKey,
            beginDate: start,
            endDate: end,
            exportObject: {
                id: objectID,
                objectType: objectType
            }
        };

        const path =`/groups/${contextGroupID}/custom-forms/export`;
        const response = await restService.post<ICustomFormsExportOptions, string>(
                path,
                exportParams,
                abortSignal
            );

        return response.data;            
    }


};

export default CustomFormsDataService;